import { useQuery } from "@apollo/client";
import { extractGraphQLError } from "@/features/review/api/errors";
import { INDICATEUR_DETAIL_QUERY } from "../api/documents";
import type { IndicateurDetailQueryResult, IndicateurDetailQueryVariables } from "../types/graphql";

/**
 * Resolves the `indicateurId` already parsed by `parseIndicateurIdParam`
 * through the existing single-id `indicateur(id)` query. Mirrors
 * `useProposalDetail`'s shape (`features/review/hooks/useProposalDetail.ts`
 * lines 19–33: `notFound` derived from `data.<field> === null`, never a
 * GraphQL error) — `indicateur(id)` returns `null`, not an error, for an
 * id that cannot be resolved (`packages/nbs-platform/src/facade/
 * application-facade.ts` `getIndicateur`: `NOT_FOUND` from the canonical
 * repository is mapped to `domainOk(null)`, never propagated as a GraphQL
 * error) — so `notFound` and `error` are two disjoint, never-confused
 * states here, exactly as required by §4.4/§4.5.
 *
 * `extractGraphQLError`/`ReviewErrorViewModel` are reused as-is from
 * `features/review/api/errors.ts` — no new error classification is
 * introduced (same reuse precedent as `useN3Mesures`).
 */
export function useN2Indicateur(indicateurId: string) {
  const { data, previousData, loading, error, refetch } = useQuery<
    IndicateurDetailQueryResult,
    IndicateurDetailQueryVariables
  >(INDICATEUR_DETAIL_QUERY, {
    variables: { id: indicateurId },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const effective = data ?? previousData;
  const isInitialLoading = loading && data === undefined && previousData === undefined;
  const graphQLError = extractGraphQLError(error);
  const notFound =
    !isInitialLoading && !graphQLError && effective !== undefined && effective.indicateur === null;

  return {
    indicateur: effective?.indicateur ?? null,
    isInitialLoading,
    notFound,
    error: graphQLError,
    refetch,
  };
}
